import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, CloudQueue as CloudQueueIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot, ReferenceLine } from 'recharts';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  buildFitColgroup,
} from '../utils/fitTable';

const CHART_COLORS = ['#1565c0', '#2e7d32', '#e65100', '#6a1b9a'];
const PASTEL_BLUE = '#e3f2fd';
const PASTEL_GREEN = '#e8f5e9';
const PASTEL_YELLOW = '#fffde7';
const PASTEL_GREY = '#f5f5f5';
const PASTEL_PURPLE = '#f3e5f5';

const PROPERTY_CODES = ['28LC', '87HP', '2SA', '67RR'];
const IRELAND_PROPS = ['28LC', '87HP', '2SA'];
const UK_PROPS = ['67RR'];
const GROSS_CURRENCY: Record<string, 'EUR' | 'GBP'> = { '28LC': 'EUR', '87HP': 'EUR', '2SA': 'EUR', '67RR': 'GBP' };
const NET_BRACKETS = [50000, 60000, 70000, 80000, 90000, 100000, 110000, 120000, 130000, 140000, 150000, 160000, 170000, 180000, 190000, 200000, 210000, 220000, 230000, 240000, 250000, 260000, 270000, 280000, 290000, 300000, 310000, 320000, 330000, 340000, 350000, 360000, 370000, 380000, 390000, 400000];
const CHART_X_TICKS = [50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000];

interface CalculatorRow {
  target_total_incl_vat?: number;
  target_total_incl_vat_orig?: number;
  actual_total_incl_vat?: number;
  net_bracket?: number;
  host_percentage?: number;
  vat_rate?: number;
  currency?: string;
  exchange_rate?: number;
}

interface ThresholdConfigRow {
  agreed_hosting_rate_pct: number;      // e.g. 20 = 20% at base bracket
  agreed_hosting_rate_discount_pct: number;  // discount from agreed rate (e.g. 0)
  target_total_incl_vat: number;        // Target Total to Owners (incl VAT) in EUR – used for calculations
  target_currency?: 'EUR' | 'GBP';      // 67RR uses GBP, others EUR
  target_total_incl_vat_orig?: number;  // When GBP: stored amount in GBP (e.g. 180000)
  bracket_amount?: number;              // legacy: net ex VAT (migrated to actual_total_to_owners_ex_vat)
  actual_total_to_owners_ex_vat: number; // Actual Total to Owners (ex VAT) - annual performance
  gross_total_incl_vat?: number;        // Col F: Gross incl VAT (user input for fee calc)
  bracket_increment: number;            // bracket step size in € (e.g. 10000 = €10k)
  delta_per_10k: number;                // % change per bracket increment (e.g. 1 = +1%)
}

interface HostingConfigData {
  calculator: Record<string, CalculatorRow>;
  thresholds?: Record<string, Record<string, number>>;  // legacy, full grid
  threshold_config?: Record<string, ThresholdConfigRow>;  // new: 5 params per property
  monthly_host_payment?: number;   // gross monthly payment to host (e.g. 9000)
  host_payment_vat_rate_ireland?: number;  // VAT rate for host payment (Ireland, e.g. 0.135)
  host_payment_vat_rate_uk?: number;       // VAT rate for host payment (UK, e.g. 0.20)
}

// From Hosting_2026.xlsx: Target (C), Gross (F), Actual ex VAT (H), Net Bracket (I), Host % (J), bracket 10k, delta 1%
// 67RR target: £180,000 GBP → EUR via exchange rate (default 1.15832)
const DEFAULT_THRESHOLD_CONFIG: Record<string, ThresholdConfigRow> = {
  '28LC': { agreed_hosting_rate_pct: 20, agreed_hosting_rate_discount_pct: 0, target_total_incl_vat: 75000, target_currency: 'EUR', actual_total_to_owners_ex_vat: 64875, gross_total_incl_vat: 75000, bracket_increment: 10000, delta_per_10k: 1 },
  '87HP': { agreed_hosting_rate_pct: 20, agreed_hosting_rate_discount_pct: 0, target_total_incl_vat: 160000, target_currency: 'EUR', actual_total_to_owners_ex_vat: 138400, gross_total_incl_vat: 160000, bracket_increment: 10000, delta_per_10k: 1 },
  '2SA':  { agreed_hosting_rate_pct: 20, agreed_hosting_rate_discount_pct: 0, target_total_incl_vat: 240000, target_currency: 'EUR', actual_total_to_owners_ex_vat: 207600, gross_total_incl_vat: 240000, bracket_increment: 10000, delta_per_10k: 1 },
  '67RR': { agreed_hosting_rate_pct: 20, agreed_hosting_rate_discount_pct: 0, target_total_incl_vat: 208497, target_currency: 'GBP', target_total_incl_vat_orig: 180000, actual_total_to_owners_ex_vat: 166798, gross_total_incl_vat: 180000, bracket_increment: 10000, delta_per_10k: 1 }
};

// S2=13.5% IRL, S3=20% UK, S4=GBP to EUR. Gross input: Ireland EUR, 67RR GBP (converted to EUR for flow).
const DEFAULT_CALCULATOR_RATES: Record<string, { vat_rate: number; currency: string; gross_currency?: string; exchange_rate?: number }> = {
  '28LC': { vat_rate: 0.135, currency: 'EUR', gross_currency: 'EUR' },
  '87HP': { vat_rate: 0.135, currency: 'EUR', gross_currency: 'EUR' },
  '2SA': { vat_rate: 0.135, currency: 'EUR', gross_currency: 'EUR' },
  '67RR': { vat_rate: 0.20, currency: 'EUR', gross_currency: 'GBP', exchange_rate: 1.15832 }
};

const defaultConfig: HostingConfigData = {
  calculator: { ...DEFAULT_CALCULATOR_RATES },
  threshold_config: { ...DEFAULT_THRESHOLD_CONFIG },
  monthly_host_payment: 9000,
  host_payment_vat_rate_ireland: 0.135,
  host_payment_vat_rate_uk: 0.20
};

const fmtCurrency = (n: number, currency: 'EUR' | 'GBP' = 'EUR') =>
  new Intl.NumberFormat('en-IE', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
/** Format GBP with EUR equivalent in same cell */
const fmtGbpWithEur = (gbp: number, eur: number) =>
  `${fmtCurrency(gbp, 'GBP')} (${fmtCurrency(eur, 'EUR')})`;
const fmtPercentFromDecimal = (n: number) => (n * 100).toFixed(1); // 0.2 -> "20"
const fmtPercentWhole = (n: number) => n.toFixed(1); // 20 -> "20"
const parseCurrency = (s: string) => parseFloat(s.replace(/[€£,\s]/g, '')) || 0;

/** Derive Actual Total to Owners (ex VAT) from Gross (incl VAT) – Euro-converted.
 *  Performance input is Gross (incl VAT); we remove VAT, floor to bracket, then compare to Net bracket. */
function getDerivedActualExVat(
  grossInCurr: number,
  grossCurr: 'EUR' | 'GBP',
  vatRate: number,
  exchangeRate: number
): number {
  if (!grossInCurr) return 0;
  const grossEur = grossCurr === 'GBP' ? grossInCurr * exchangeRate : grossInCurr;
  return grossEur * (1 - vatRate);
}

/** Derive pivot net (ex VAT) from target_total_incl_vat, floored to bracket (Excel col I).
 *  Excel: Target ex VAT = Target incl VAT × (1 − VAT rate), pivot = floor(ex VAT, increment) */
function targetToPivotBracket(targetInclVat: number, vatRate: number, increment: number): number {
  if (!targetInclVat || increment <= 0) return 0;
  const targetExVat = vatRate > 0 ? targetInclVat * (1 - vatRate) : targetInclVat;
  return Math.floor(targetExVat / increment) * increment;
}

/** Calculate host % for a bracket from threshold_config (matches Excel thresholds).
 *  netBracket = floored performance Net (ex VAT); pivotNet = floored target Net (ex VAT). Both are NET. */
function calcThresholdRate(
  cfg: ThresholdConfigRow | undefined,
  netBracket: number,
  vatRate: number = 0.20
): number {
  if (!cfg) return 0;
  const increment = cfg.bracket_increment && cfg.bracket_increment > 0 ? cfg.bracket_increment : 10000;
  const pivotNet = cfg.target_total_incl_vat != null
    ? targetToPivotBracket(cfg.target_total_incl_vat, vatRate, increment)
    : (cfg.bracket_amount ?? 0);
  if (pivotNet <= 0) return 0;
  const effectiveRate = (cfg.agreed_hosting_rate_pct - cfg.agreed_hosting_rate_discount_pct) / 100;
  const steps = (netBracket - pivotNet) / increment;
  const delta = (cfg.delta_per_10k ?? 0) / 100;
  return Math.max(0, Math.min(1, effectiveRate + steps * delta));
}

const SAVE_DEBOUNCE_MS = 1500;

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

export const HostingCalculator: React.FC = () => {
  const [year, setYear] = useState(2026);
  const [config, setConfig] = useState<HostingConfigData>(defaultConfig);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string>('');
  const saveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isInitialLoad.current = true;
    fetchConfig();
  }, [year]);

  const isInitialLoad = React.useRef(true);
  const saveToDb = React.useCallback(async () => {
    setSaveStatus('saving');
    try {
      setError('');
      // Only persist variables (yellow cells). Omit legacy thresholds grid (calculations).
      const toSave = {
        calculator: config.calculator,
        threshold_config: config.threshold_config,
        monthly_host_payment: config.monthly_host_payment ?? 9000,
        host_payment_vat_rate_ireland: config.host_payment_vat_rate_ireland ?? 0.135,
        host_payment_vat_rate_uk: config.host_payment_vat_rate_uk ?? 0.20
      };
      try {
        await api.put(`/api/hosting-variables/${year}`, { config: toSave });
      } catch (putErr: any) {
        if (putErr.response?.status === 404) {
          await api.post('/api/hosting-variables/', { year, config: toSave });
        } else {
          throw putErr;
        }
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
      setSaveStatus('idle');
      console.error(err);
    }
  }, [year, config]);

  useEffect(() => {
    if (loading || !config) return;
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setSaveStatus('pending');
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveToDb();
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [config, loading, saveToDb]);

  useEffect(() => {
    if (!loading) isInitialLoad.current = false;
  }, [loading]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/hosting-variables/${year}`);
      let loaded = response.data.config || defaultConfig;
      // Ensure threshold_config exists (merge defaults if missing, e.g. from legacy thresholds-only config)
      if (!loaded.threshold_config || Object.keys(loaded.threshold_config).length === 0) {
        loaded.threshold_config = { ...DEFAULT_THRESHOLD_CONFIG };
      } else {
        // Fill in missing properties with defaults; migrate bracket_amount -> actual_total_to_owners_ex_vat / target_total_incl_vat
        const merged: Record<string, ThresholdConfigRow> = { ...DEFAULT_THRESHOLD_CONFIG };
        for (const code of PROPERTY_CODES) {
          const loadedRow = loaded.threshold_config[code];
          const def = DEFAULT_THRESHOLD_CONFIG[code];
          if (loadedRow) {
            let row: ThresholdConfigRow = { ...def, ...loadedRow };
            if (row.bracket_amount != null && row.actual_total_to_owners_ex_vat == null) {
              row = { ...row, actual_total_to_owners_ex_vat: row.bracket_amount };
            }
            if (row.target_total_incl_vat == null && row.bracket_amount != null) {
              const vat = loaded.calculator?.[code]?.vat_rate ?? 0.20;
              row = { ...row, target_total_incl_vat: Math.round(row.bracket_amount * (1 + vat)) };
            }
            if (row.gross_total_incl_vat == null && row.actual_total_to_owners_ex_vat != null) {
              const vat = loaded.calculator?.[code]?.vat_rate ?? 0.20;
              row = { ...row, gross_total_incl_vat: Math.round(row.actual_total_to_owners_ex_vat / (1 - vat)) };
            }
            // 67RR: migrate gross from EUR to GBP if stored as EUR (legacy)
            if (code === '67RR' && row.gross_total_incl_vat != null) {
              const grossCurrency = (loaded.calculator?.[code] as Record<string, unknown>)?.gross_currency;
              const rate = parseFloat((loaded.calculator?.[code] as Record<string, unknown>)?.exchange_rate as string) || 1.15832;
              if (grossCurrency !== 'GBP') {
                row = { ...row, gross_total_incl_vat: Math.round(row.gross_total_incl_vat! / rate) };
              }
            }
            merged[code] = row;
          }
        }
        loaded.threshold_config = merged;
      }
      // Ensure calculator has vat_rate, currency, exchange_rate per property (governmental figures from DB)
      if (!loaded.calculator) loaded.calculator = {};
      for (const code of PROPERTY_CODES) {
        const def = DEFAULT_CALCULATOR_RATES[code];
        if (!loaded.calculator[code]) loaded.calculator[code] = { ...def };
        else {
          const row = loaded.calculator[code] as Record<string, unknown>;
          if (row.vat_rate == null) row.vat_rate = def.vat_rate;
          if (row.currency == null) row.currency = def.currency;
          if (row.exchange_rate == null && def.exchange_rate) row.exchange_rate = def.exchange_rate;
          if (row.gross_currency == null) row.gross_currency = def.gross_currency ?? GROSS_CURRENCY[code];
        }
      }
      // Fetch GBP/EUR rate on page load and update 67RR exchange_rate + target_total_incl_vat
      try {
        const rateRes = await api.get('/api/hosting-variables/exchange-rate/gbp-eur');
        const rate = rateRes.data?.rate;
        if (typeof rate === 'number' && rate > 0) {
          const calc = loaded.calculator?.['67RR'] as Record<string, unknown> | undefined;
          const prevRate = calc ? parseFloat(String(calc.exchange_rate ?? 1.15832)) : 1.15832;
          const rateChanged = Math.abs(rate - prevRate) > 0.0001;
          if (calc) {
            loaded = {
              ...loaded,
              calculator: { ...loaded.calculator, '67RR': { ...calc, exchange_rate: rate } }
            };
          }
          const tc = loaded.threshold_config?.['67RR'];
          if (tc) {
            const orig = tc.target_total_incl_vat_orig ?? 180000;
            const targetEur = Math.round(orig * rate);
            loaded = {
              ...loaded,
              threshold_config: {
                ...loaded.threshold_config,
                '67RR': { ...tc, target_total_incl_vat: targetEur, target_total_incl_vat_orig: orig, target_currency: 'GBP' as const }
              }
            };
          }
          if (rateChanged && loaded.calculator?.['67RR']) {
            await api.put(`/api/hosting-variables/${year}`, { config: { calculator: loaded.calculator, threshold_config: loaded.threshold_config } });
          }
        }
      } catch (_) {
        // Use existing rate if fetch fails
      }
      if (loaded.monthly_host_payment == null) loaded.monthly_host_payment = 9000;
      if (loaded.host_payment_vat_rate_ireland == null) loaded.host_payment_vat_rate_ireland = 0.135;
      if (loaded.host_payment_vat_rate_uk == null) loaded.host_payment_vat_rate_uk = (loaded as Record<string, unknown>).host_payment_vat_rate ?? 0.20;
      setConfig(loaded);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setConfig(defaultConfig);
      } else {
        setError('Failed to load hosting config');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCalculator = (propertyCode: string, field: string, value: number | string) => {
    setConfig(prev => ({
      ...prev,
      calculator: {
        ...prev.calculator,
        [propertyCode]: {
          ...(prev.calculator[propertyCode] || {}),
          [field]: typeof value === 'string' ? value : (isNaN(value as number) ? undefined : value)
        }
      }
    }));
  };

  const updateThresholdConfig = (propertyCode: string, field: keyof ThresholdConfigRow, value: number) => {
    setConfig(prev => ({
      ...prev,
      threshold_config: {
        ...prev.threshold_config,
        [propertyCode]: {
          ...(prev.threshold_config?.[propertyCode] || DEFAULT_THRESHOLD_CONFIG[propertyCode]),
          [field]: isNaN(value) ? 0 : value
        }
      }
    }));
  };

  const getThresholdConfigValue = (propertyCode: string, field: keyof ThresholdConfigRow): number | string => {
    const row = config.threshold_config?.[propertyCode];
    if (!row) return field === 'delta_per_10k' ? '1' : field === 'bracket_increment' ? '10000' : '';
    const val = row[field];
    return val !== undefined && val !== null ? String(val) : '';
  };

  const getCalculatorValue = (propertyCode: string, field: string): number | string => {
    const row = config.calculator[propertyCode];
    if (!row) return '';
    const val = (row as Record<string, unknown>)[field];
    return val !== undefined && val !== null ? String(val) : '';
  };

  const getCalculatedThreshold = (propertyCode: string, netAmount: number): number => {
    const vat = config.calculator[propertyCode]?.vat_rate ?? 0.20;
    return calcThresholdRate(config.threshold_config?.[propertyCode], netAmount, vat);
  };

  const hostFeeByProperty = useMemo(() => {
    const rows: Array<{ code: string; hostNet: number; hostGross: number; rate?: number }> = [];
    PROPERTY_CODES.forEach((code) => {
      const grossInCurr = parseFloat(getThresholdConfigValue(code, 'gross_total_incl_vat') as string) || 0;
      const grossCurr = (getCalculatorValue(code, 'gross_currency') || GROSS_CURRENCY[code]) as 'EUR' | 'GBP';
      const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || (code === '67RR' ? 1.15832 : 1);
      const gross = grossCurr === 'GBP' ? grossInCurr * rate : grossInCurr;
      const vatRate = parseFloat(getCalculatorValue(code, 'vat_rate') as string) || 0.20;
      const increment = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 10000;
      const netExVat = gross > 0 ? gross * (1 - vatRate) : 0;
      const netBracket = netExVat > 0 && increment > 0 ? Math.floor(netExVat / increment) * increment : 0;
      const hostPctDec = getCalculatedThreshold(code, netBracket);
      const hostNet = netExVat * hostPctDec;
      const vatOnHost = hostNet * vatRate;
      const hostGross = hostNet + vatOnHost;
      rows.push({ code, hostNet, hostGross, rate: code === '67RR' ? rate : undefined });
    });
    const ireland = rows.filter((r) => IRELAND_PROPS.includes(r.code));
    const uk = rows.filter((r) => UK_PROPS.includes(r.code));
    const hostGross = ireland.reduce((s, r) => s + r.hostGross, 0) + uk.reduce((s, r) => s + r.hostGross, 0);
    const hostNet = ireland.reduce((s, r) => s + r.hostNet, 0) + uk.reduce((s, r) => s + r.hostNet, 0);
    return { rows, hostGross, hostNet };
  }, [config]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0, pb: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Performance" id="hosting-tab-0" />
        <Tab label="Configuration" id="hosting-tab-1" />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
      {/* Gross (Col F) Input - main user input */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6">Properties Performance</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              setConfig(prev => {
                const next = { ...prev, threshold_config: { ...prev.threshold_config! } };
                PROPERTY_CODES.forEach((code) => {
                  const tc = next.threshold_config![code];
                  if (!tc) return;
                  const targetCurr = (tc.target_currency as string) || GROSS_CURRENCY[code];
                  const targetVal = targetCurr === 'GBP' && (tc.target_total_incl_vat_orig ?? 0) > 0
                    ? tc.target_total_incl_vat_orig!
                    : tc.target_total_incl_vat ?? 0;
                  next.threshold_config![code] = { ...tc, gross_total_incl_vat: targetVal };
                });
                return next;
              });
            }}
          >
            Reset to defaults
          </Button>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {saveStatus === 'saving' && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CloudQueueIcon fontSize="small" /> Saving variables…
            </Typography>
          )}
          {saveStatus === 'saved' && (
            <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon fontSize="small" /> Saved
            </Typography>
          )}
          {saveStatus === 'pending' && (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CloudQueueIcon fontSize="small" /> Unsaved changes
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Enter the Gross figure for each property to see the corresponding hosting fee in the sections below.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
        {PROPERTY_CODES.map((code) => {
          const curr = (getCalculatorValue(code, 'gross_currency') || GROSS_CURRENCY[code]) as 'EUR' | 'GBP';
          const v = parseFloat(getThresholdConfigValue(code, 'gross_total_incl_vat') as string) || 0;
          const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || (code === '67RR' ? 1.15832 : 1);
          const eurEquivalent = curr === 'GBP' && v ? v * rate : 0;
          return (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={code} sx={{ display: 'flex' }}>
              <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 110, bgcolor: 'warning.light', '& .MuiCardContent-root': { py: 1, px: 1.5, flex: 1, '&:last-child': { pb: 1 } } }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.25 }}>{code}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Gross (incl VAT)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="0"
                      value={v ? v.toLocaleString('en-IE', { maximumFractionDigits: 0 }) : ''}
                      onChange={(e) => updateThresholdConfig(code, 'gross_total_incl_vat', parseCurrency(e.target.value))}
                      inputProps={{ style: { textAlign: 'right' } }}
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 72 }}>
                      <Select
                        value={curr}
                        onChange={(e) => updateCalculator(code, 'gross_currency', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ minHeight: 20, mt: 0.25 }}>
                    {curr === 'GBP' && v > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {fmtCurrency(eurEquivalent, 'EUR')}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Performance-based Hosting Fee – columns F–J from Gross */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Performance-based Hosting Fee</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        How the hosting fee is calculated from Gross Performance (columns F–J).
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, mb: 3 }}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <FitColgroup
            cols={equalFitCols(
              ['property', 'tIncl', 'tVat', 'tEx', 'aIncl', 'aVat', 'aEx', 'net', 'host'],
              { flexKey: 'property' }
            )}
          />
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Property</TableCell>
              <TableCell align="center">Target<br />(incl VAT)</TableCell>
              <TableCell align="center">VAT</TableCell>
              <TableCell align="center" sx={{ bgcolor: PASTEL_YELLOW }}>Target<br />(ex VAT)</TableCell>
              <TableCell align="center">Actual<br />(incl VAT)</TableCell>
              <TableCell align="center">VAT</TableCell>
              <TableCell align="center" sx={{ bgcolor: PASTEL_YELLOW }}>Actual<br />(ex VAT)</TableCell>
              <TableCell align="center">Net Bracket</TableCell>
              <TableCell align="center" sx={{ bgcolor: PASTEL_PURPLE }}>Host %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const rows: Array<{ code: string; vatRate: number; targetInclVat: number; targetExVat: number; targetVat: number; actualInclVat: number; actualExVat: number; actualVat: number; netBracket: number; hostPct: number; grossInCurr?: number; grossCurr?: 'EUR' | 'GBP'; rate: number }> = [];
              PROPERTY_CODES.forEach((code) => {
                const vatRate = parseFloat(getCalculatorValue(code, 'vat_rate') as string) || 0.20;
                const targetInclVat = parseFloat(getThresholdConfigValue(code, 'target_total_incl_vat') as string) || 0;
                const grossInCurr = parseFloat(getThresholdConfigValue(code, 'gross_total_incl_vat') as string) || 0;
                const grossCurr = (getCalculatorValue(code, 'gross_currency') || GROSS_CURRENCY[code]) as 'EUR' | 'GBP';
                const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || (code === '67RR' ? 1.15832 : 1);
                const increment = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 10000;
                const targetVat = targetInclVat * vatRate;
                const targetExVat = targetInclVat * (1 - vatRate);
                const actualInclVat = grossCurr === 'GBP' ? grossInCurr * rate : grossInCurr;
                const actualExVat = getDerivedActualExVat(grossInCurr, grossCurr, vatRate, rate);
                const actualVat = actualInclVat * vatRate;
                const netBracket = increment > 0 ? Math.floor(actualExVat / increment) * increment : actualExVat;
                const hostPct = Math.round(getCalculatedThreshold(code, netBracket) * 1000) / 10;
                rows.push({ code, vatRate, targetInclVat, targetExVat, targetVat, actualInclVat, actualExVat, actualVat, netBracket, hostPct, grossInCurr: grossCurr === 'GBP' ? grossInCurr : undefined, grossCurr: grossCurr === 'GBP' ? grossCurr : undefined, rate });
              });
              const irelandRows = rows.filter((r) => IRELAND_PROPS.includes(r.code));
              const ukRows = rows.filter((r) => UK_PROPS.includes(r.code));
              const irelandExVat = irelandRows.reduce((s, r) => s + r.actualExVat, 0);
              const irelandHostNet = irelandRows.reduce((s, r) => s + r.actualExVat * (r.hostPct / 100), 0);
              const ukExVat = ukRows.reduce((s, r) => s + r.actualExVat, 0);
              const ukHostNet = ukRows.reduce((s, r) => s + r.actualExVat * (r.hostPct / 100), 0);
              const ireland = { targetInclVat: irelandRows.reduce((s, r) => s + r.targetInclVat, 0), targetVat: irelandRows.reduce((s, r) => s + r.targetVat, 0), targetExVat: irelandRows.reduce((s, r) => s + r.targetExVat, 0), actualInclVat: irelandRows.reduce((s, r) => s + r.actualInclVat, 0), actualVat: irelandRows.reduce((s, r) => s + r.actualVat, 0), actualExVat: irelandExVat, netBracket: 0, hostPct: irelandExVat > 0 ? Math.round((irelandHostNet / irelandExVat) * 1000) / 10 : 20 };
              const uk = { targetInclVat: ukRows.reduce((s, r) => s + r.targetInclVat, 0), targetVat: ukRows.reduce((s, r) => s + r.targetVat, 0), targetExVat: ukRows.reduce((s, r) => s + r.targetExVat, 0), actualInclVat: ukRows.reduce((s, r) => s + r.actualInclVat, 0), actualVat: ukRows.reduce((s, r) => s + r.actualVat, 0), actualExVat: ukExVat, netBracket: 0, hostPct: ukExVat > 0 ? Math.round((ukHostNet / ukExVat) * 1000) / 10 : 20 };
              const globalExVat = irelandExVat + ukExVat;
              const globalHostNet = irelandHostNet + ukHostNet;
              const global = { targetInclVat: ireland.targetInclVat + uk.targetInclVat, targetVat: ireland.targetVat + uk.targetVat, targetExVat: ireland.targetExVat + uk.targetExVat, actualInclVat: ireland.actualInclVat + uk.actualInclVat, actualVat: ireland.actualVat + uk.actualVat, actualExVat: globalExVat, netBracket: 0, hostPct: globalExVat > 0 ? Math.round((globalHostNet / globalExVat) * 1000) / 10 : 20 };
              const rate67RR = rows.find((x) => x.code === '67RR')?.rate ?? 1;
              const renderRow = (r: typeof rows[0] | typeof ireland, code: string, isTotal: boolean) => {
                const is67RR = code === '67RR' && !['Ireland Total', 'UK Total', 'Global Total'].includes(code);
                const dual = (eurVal: number, gbpOverride?: number) => {
                  if (!eurVal) return '—';
                  if (is67RR && rate67RR) {
                    const gbp = gbpOverride ?? eurVal / rate67RR;
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                        <Typography variant="body2">{fmtCurrency(gbp, 'GBP')}</Typography>
                        <Typography variant="caption" color="text.secondary">{fmtCurrency(eurVal, 'EUR')}</Typography>
                      </Box>
                    );
                  }
                  return fmtCurrency(eurVal, 'EUR');
                };
                return (
                <TableRow key={code} sx={isTotal ? { '& .MuiTableCell-root': { fontWeight: 600 } } : {}}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{code}</TableCell>
                  <TableCell align="right">{'targetInclVat' in r && r.targetInclVat ? dual(r.targetInclVat, Math.round(r.targetInclVat / rate67RR / 1000) * 1000) : '—'}</TableCell>
                  <TableCell align="right">{'targetVat' in r && r.targetVat ? dual(r.targetVat) : '—'}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: PASTEL_YELLOW }}>{'targetExVat' in r && r.targetExVat ? dual(r.targetExVat) : '—'}</TableCell>
                  <TableCell align="right">{'actualInclVat' in r && r.actualInclVat ? dual(r.actualInclVat, 'grossInCurr' in r ? r.grossInCurr : undefined) : '—'}</TableCell>
                  <TableCell align="right">{'actualVat' in r && r.actualVat ? dual(r.actualVat) : '—'}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: PASTEL_YELLOW }}>{'actualExVat' in r && r.actualExVat ? dual(r.actualExVat) : '—'}</TableCell>
                  <TableCell align="right">{'netBracket' in r && r.netBracket ? dual(r.netBracket) : '—'}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: PASTEL_PURPLE }}>{'hostPct' in r && r.hostPct ? `${r.hostPct}%` : '—'}</TableCell>
                </TableRow>
                );
              };
              return (
                <>
                  {rows.map((r) => renderRow(r, r.code, false))}
                  {renderRow(ireland, 'Ireland Total', true)}
                  {renderRow(uk, 'UK Total', true)}
                  {renderRow(global, 'Global Total', true)}
                </>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Calculated hosting fee rates – one chart per property (using the config tab settings)</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {PROPERTY_CODES.map((code, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          const grossInCurr = parseFloat(getThresholdConfigValue(code, 'gross_total_incl_vat') as string) || 0;
          const grossCurr = (getCalculatorValue(code, 'gross_currency') || GROSS_CURRENCY[code]) as 'EUR' | 'GBP';
          const vatRate = parseFloat(getCalculatorValue(code, 'vat_rate') as string) || 0.20;
          const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || (code === '67RR' ? 1.15832 : 1);
          const increment = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 10000;
          const actual = getDerivedActualExVat(grossInCurr, grossCurr, vatRate, rate);
          const netBracket = increment > 0 ? Math.floor(actual / increment) * increment : actual;
          const hostPct = actual > 0 ? Math.round(getCalculatedThreshold(code, netBracket) * 1000) / 10 : 0;
          const chartData = NET_BRACKETS.map((net) => ({
            net,
            rate: Math.round(getCalculatedThreshold(code, net) * 1000) / 10
          }));
          return (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={code} sx={{ minWidth: 0 }}>
              <Paper sx={{ p: 1, width: '100%', overflow: 'hidden', boxSizing: 'border-box', position: 'relative' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75, textAlign: 'center', display: 'block', width: '100%' }}>{code} Performance</Typography>
                <Box sx={{ position: 'relative', height: 280 }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData} margin={{ top: 8, right: 35, left: 25, bottom: 55 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="net" ticks={CHART_X_TICKS} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : String(v))} angle={-45} tick={{ textAnchor: 'end', fontSize: 11 }} interval={0} label={{ value: 'Net (ex VAT)', position: 'bottom', offset: 35, style: { fontSize: 12 } }} />
                      <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} tick={{ fontSize: 11 }} width={28} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0]?.payload as { net: number; rate: number } | undefined;
                        if (!p) return null;
                        const isActualBracket = p.net === netBracket;
                        return (
                          <Paper sx={{ p: 1, fontSize: 12 }}>
                            <Box>Net (bracket): {fmtCurrency(p.net, 'EUR')}</Box>
                            <Box sx={{ fontWeight: 600 }}>Host %: {p.rate}%</Box>
                            {isActualBracket && actual > 0 && (
                              <Box sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary' }}>
                                Actual: {fmtCurrency(actual, 'EUR')} net
                              </Box>
                            )}
                          </Paper>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke={color}
                      strokeWidth={2}
                      dot={(props: { cx?: number; cy?: number; payload?: { net: number } }) => {
                        const { cx = 0, cy = 0, payload } = props;
                        const isKeyBracket = payload?.net === netBracket;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={isKeyBracket ? 5 : 1}
                            fill={isKeyBracket ? color : 'none'}
                            stroke={color}
                            strokeWidth={isKeyBracket ? 2 : 1}
                          />
                        );
                      }}
                      activeDot={{ r: 5 }}
                    />
                    {actual > 0 && (
                      <>
                        <ReferenceLine segment={[{ x: actual, y: 0 }, { x: actual, y: hostPct }]} stroke={color} strokeDasharray="5 5" strokeWidth={2} />
                        <ReferenceLine x={actual} stroke="none" label={{ value: actual >= 1000 ? `${Math.round(actual / 1000)}k` : String(actual), position: 'insideBottom', fill: color, fontSize: 11, fontWeight: 600 }} />
                        <ReferenceLine y={hostPct} stroke={color} strokeDasharray="5 5" strokeWidth={2} label={{ value: `${hostPct}%`, position: 'right', fill: color, fontSize: 11, fontWeight: 600 }} />
                        <ReferenceDot x={actual} y={hostPct} r={6} fill={color} stroke="#fff" strokeWidth={2} />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Flow: Gross (F) → VAT (G) → Net ex VAT (H) → Bracket (I) → Host% (J) → Host Net (K) → Host Gross (M) → Net to Owners (Q) */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Income Funnel</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Step-by-step from 'Gross Income' to 'Net Total to Owners after Hosting Fee'.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, mb: 3 }}>
        <Table
          size="small"
          sx={{
            ...FIT_TABLE_SX,
            fontSize: '0.7rem',
            '& .MuiTableCell-root': {
              ...FIT_TABLE_SX['& .MuiTableCell-root'],
              py: 0.5,
              px: 0.75,
              fontSize: '0.7rem',
            },
          }}
        >
          <FitColgroup
            cols={[
              { key: 'metric', pct: 14 },
              ...[...PROPERTY_CODES, 'Ireland Total', 'UK Total', 'Global Total'].flatMap((col) => [
                { key: `${col}-pa`, pct: 6.14 },
                { key: `${col}-pm`, pct: 6.14 },
              ]),
            ]}
          />
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Metric</TableCell>
              {[...PROPERTY_CODES, 'Ireland Total', 'UK Total', 'Global Total'].map((col) => (
                <TableCell key={col} colSpan={2} align="center" sx={{ fontWeight: 'bold', borderLeft: col !== '28LC' ? 1 : 0, borderColor: 'divider' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TableCell />
              {[...PROPERTY_CODES, 'Ireland Total', 'UK Total', 'Global Total'].map((col) => (
                <React.Fragment key={col}>
                  <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.65rem', borderLeft: col !== '28LC' ? 1 : 0, borderColor: 'divider' }}>p/a</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.65rem', borderColor: 'divider' }}>p/m</TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const rows: Array<{ code: string; gross: number; grossInCurr?: number; grossCurr?: 'EUR' | 'GBP'; rate: number; vatAmount: number; netExVat: number; netBracket: number; hostPctDec: number; hostNet: number; vatOnHost: number; hostGross: number; hostMoGross: number; netToOwners: number }> = [];
              PROPERTY_CODES.forEach((code) => {
                const grossInCurr = parseFloat(getThresholdConfigValue(code, 'gross_total_incl_vat') as string) || 0;
                const grossCurr = (getCalculatorValue(code, 'gross_currency') || GROSS_CURRENCY[code]) as 'EUR' | 'GBP';
                const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || (code === '67RR' ? 1.15832 : 1);
                const gross = grossCurr === 'GBP' ? grossInCurr * rate : grossInCurr;
                const vatRate = parseFloat(getCalculatorValue(code, 'vat_rate') as string) || 0.20;
                const increment = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 10000;
                const vatAmount = gross > 0 ? gross * vatRate : 0;
                const netExVat = gross > 0 ? gross * (1 - vatRate) : 0;
                const netBracket = netExVat > 0 && increment > 0 ? Math.floor(netExVat / increment) * increment : 0;
                const hostPctDec = getCalculatedThreshold(code, netBracket);
                const hostNet = netExVat * hostPctDec;
                const vatOnHost = hostNet * vatRate;
                const hostGross = hostNet + vatOnHost;
                const netToOwners = netExVat - hostNet;
                const hostMoGross = hostGross / 12;
                rows.push({ code, gross, grossInCurr: grossCurr === 'GBP' ? grossInCurr : undefined, grossCurr: grossCurr === 'GBP' ? grossCurr : undefined, rate, vatAmount, netExVat, netBracket, hostPctDec, hostNet, vatOnHost, hostGross, hostMoGross, netToOwners });
              });
              const irelandRows = rows.filter((r) => IRELAND_PROPS.includes(r.code));
              const ukRows = rows.filter((r) => UK_PROPS.includes(r.code));
              const ireland = { gross: irelandRows.reduce((s, r) => s + r.gross, 0), vatAmount: irelandRows.reduce((s, r) => s + r.vatAmount, 0), netExVat: irelandRows.reduce((s, r) => s + r.netExVat, 0), hostNet: irelandRows.reduce((s, r) => s + r.hostNet, 0), hostGross: irelandRows.reduce((s, r) => s + r.hostGross, 0), netToOwners: irelandRows.reduce((s, r) => s + r.netToOwners, 0) };
              const uk = { gross: ukRows.reduce((s, r) => s + r.gross, 0), vatAmount: ukRows.reduce((s, r) => s + r.vatAmount, 0), netExVat: ukRows.reduce((s, r) => s + r.netExVat, 0), hostNet: ukRows.reduce((s, r) => s + r.hostNet, 0), hostGross: ukRows.reduce((s, r) => s + r.hostGross, 0), netToOwners: ukRows.reduce((s, r) => s + r.netToOwners, 0) };
              const global = { gross: ireland.gross + uk.gross, vatAmount: ireland.vatAmount + uk.vatAmount, netExVat: ireland.netExVat + uk.netExVat, hostNet: ireland.hostNet + uk.hostNet, hostGross: ireland.hostGross + uk.hostGross, netToOwners: ireland.netToOwners + uk.netToOwners };
              const metricRows: Array<{ label: React.ReactNode; key: keyof typeof rows[0]; isPct?: boolean; isBracket?: boolean }> = [
                { label: <><Box component="span" sx={{ whiteSpace: 'nowrap' }}>Gross</Box><br /><Box component="span">(incl VAT)</Box></>, key: 'gross' },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>VAT</Box>, key: 'vatAmount' },
                { label: <><Box component="span" sx={{ whiteSpace: 'nowrap' }}>Net</Box><br /><Box component="span">(ex VAT)</Box></>, key: 'netExVat' },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Net Bracket</Box>, key: 'netBracket', isBracket: true },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Host %</Box>, key: 'hostPctDec', isPct: true },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Host Fee (net)</Box>, key: 'hostNet' },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>VAT on Host</Box>, key: 'vatOnHost' },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Host Fee (gross)</Box>, key: 'hostGross' },
                { label: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Net to Owners</Box>, key: 'netToOwners' }
              ];
              type MetricKey = typeof metricRows[0]['key'];
              const dualGbpEur = (gbp: number, eur: number) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                  <Typography variant="body2">{fmtCurrency(gbp, 'GBP')}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtCurrency(eur, 'EUR')}</Typography>
                </Box>
              );
              const colData: Array<{ code: string; getAnn: (k: MetricKey) => React.ReactNode; getMo: (k: MetricKey) => React.ReactNode }> = [
                ...rows.map((r) => {
                  const is67RR = r.code === '67RR' && r.grossCurr === 'GBP' && r.rate;
                  const dual = (eurVal: number, gbpOverride?: number): React.ReactNode =>
                    is67RR && eurVal ? dualGbpEur(gbpOverride ?? eurVal / r.rate, eurVal) : (eurVal ? fmtCurrency(eurVal, 'EUR') : '—');
                  return {
                    code: r.code,
                    getAnn: (k: MetricKey) => {
                      if (k === 'gross') return r.gross ? (r.grossCurr === 'GBP' && r.grossInCurr ? dualGbpEur(r.grossInCurr, r.gross) : fmtCurrency(r.gross, 'EUR')) : '—';
                      const v = r[k];
                      return v != null && typeof v === 'number' ? (k === 'hostPctDec' ? `${(v * 100).toFixed(1)}%` : dual(v)) : '—';
                    },
                    getMo: (k: MetricKey) => {
                      if (k === 'hostPctDec' || k === 'netBracket') return r[k] != null && typeof r[k] === 'number' ? (k === 'hostPctDec' ? `${((r[k] as number) * 100).toFixed(1)}%` : dual(r[k] as number)) : '—';
                      const v = r[k];
                      return v != null && typeof v === 'number' && v > 0 ? dual(v / 12) : '—';
                    }
                  };
                }),
                { code: 'Ireland Total', getAnn: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = ireland[k as keyof typeof ireland]; return v != null && typeof v === 'number' ? fmtCurrency(v, 'EUR') : '—'; }, getMo: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = ireland[k as keyof typeof ireland]; return v != null && typeof v === 'number' && v > 0 ? fmtCurrency(v / 12, 'EUR') : '—'; } },
                { code: 'UK Total', getAnn: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = uk[k as keyof typeof uk]; if (v == null || typeof v !== 'number') return '—'; const rate = rows.find((x) => x.code === '67RR')?.rate ?? 1; return dualGbpEur(v / rate, v); }, getMo: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = uk[k as keyof typeof uk]; if (v == null || typeof v !== 'number' || v <= 0) return '—'; const rate = rows.find((x) => x.code === '67RR')?.rate ?? 1; return dualGbpEur((v / 12) / rate, v / 12); } },
                { code: 'Global Total', getAnn: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = global[k as keyof typeof global]; return v != null && typeof v === 'number' ? fmtCurrency(v, 'EUR') : '—'; }, getMo: (k: MetricKey) => { if (k === 'hostPctDec' || k === 'netBracket') return '—'; const v = global[k as keyof typeof global]; return v != null && typeof v === 'number' && v > 0 ? fmtCurrency(v / 12, 'EUR') : '—'; } }
              ];
              return metricRows.map(({ label, key, isPct, isBracket }) => (
                <TableRow key={key} sx={key === 'netToOwners' ? { '& .MuiTableCell-root': { bgcolor: PASTEL_GREEN, fontWeight: 'bold' } } : {}}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{label}</TableCell>
                  {colData.map((c, ci) => {
                    const isTotal = ['Ireland Total', 'UK Total', 'Global Total'].includes(c.code);
                    const cellBg = isTotal ? (c.code === 'Global Total' ? PASTEL_BLUE : PASTEL_GREY) : undefined;
                    return (
                      <React.Fragment key={c.code}>
                        <TableCell align="right" sx={{ borderLeft: ci > 0 ? 1 : 0, borderColor: 'divider', bgcolor: cellBg }}>{c.getAnn(key)}</TableCell>
                        <TableCell align="right" sx={{ borderColor: 'divider', bgcolor: cellBg }}>{(isPct || isBracket) ? c.getAnn(key) : c.getMo(key)}</TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              ));
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Annual Payment */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Annual Payment</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        At year end, the Hosting Fee above less that already paid (i.e. <Box component="span" sx={{ fontWeight: 600 }}>{fmtCurrency(config.monthly_host_payment ?? 9000, 'EUR')}</Box> per month) is to be paid to the Host.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, mb: 3, maxWidth: 520 }}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <FitColgroup
            cols={buildFitColgroup([
              { key: 'label' },
              { key: 'gross', pct: 28 },
              { key: 'net', pct: 28 },
            ])}
          />
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} />
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gross (incl VAT)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net (ex VAT)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hostFeeByProperty.rows.map((r) => {
              const is67RR = r.code === '67RR' && r.rate;
              const cellContent = (eurVal: number) =>
                is67RR && r.rate ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                    <Typography variant="body2">{fmtCurrency(eurVal / r.rate, 'GBP')}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtCurrency(eurVal, 'EUR')}</Typography>
                  </Box>
                ) : (
                  fmtCurrency(eurVal, 'EUR')
                );
              return (
                <TableRow key={r.code}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.code}: Host fee</TableCell>
                  <TableCell align="right">{cellContent(r.hostGross)}</TableCell>
                  <TableCell align="right">{cellContent(r.hostNet)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableCell sx={{ fontWeight: 600 }}>Host fee total</TableCell>
              <TableCell align="right">{fmtCurrency(hostFeeByProperty.hostGross, 'EUR')}</TableCell>
              <TableCell align="right">{fmtCurrency(hostFeeByProperty.hostNet, 'EUR')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Amount already paid<br />(monthly × 12)</TableCell>
              <TableCell align="right">{fmtCurrency((config.monthly_host_payment ?? 9000) * 12, 'EUR')}</TableCell>
              <TableCell align="right">{fmtCurrency((() => {
                const paidGross = (config.monthly_host_payment ?? 9000) * 12;
                const irelandHostFee = hostFeeByProperty.rows.filter((r) => IRELAND_PROPS.includes(r.code)).reduce((s, r) => s + r.hostGross, 0);
                const ukHostFee = hostFeeByProperty.rows.filter((r) => UK_PROPS.includes(r.code)).reduce((s, r) => s + r.hostGross, 0);
                const total = irelandHostFee + ukHostFee;
                const effectiveVat = total > 0
                  ? (irelandHostFee * (config.host_payment_vat_rate_ireland ?? 0.135) + ukHostFee * (config.host_payment_vat_rate_uk ?? 0.20)) / total
                  : 0.20;
                return paidGross / (1 + effectiveVat);
              })(), 'EUR')}</TableCell>
            </TableRow>
            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold', bgcolor: PASTEL_GREEN } }}>
              <TableCell>Annual payment</TableCell>
              {(() => {
                const paidGross = (config.monthly_host_payment ?? 9000) * 12;
                const bonusGross = Math.max(0, hostFeeByProperty.hostGross - paidGross);
                const effectiveGrossToNet = hostFeeByProperty.hostGross > 0 ? hostFeeByProperty.hostNet / hostFeeByProperty.hostGross : 1;
                const bonusNet = bonusGross * effectiveGrossToNet;
                return (
                  <>
                    <TableCell align="right">{fmtCurrency(bonusGross, 'EUR')}</TableCell>
                    <TableCell align="right">{fmtCurrency(bonusNet, 'EUR')}</TableCell>
                  </>
                );
              })()}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Hosting Fee Variables</Typography>
            <Typography variant="body2" color="text.secondary">
              Agreed hosting rate, discount, target bracket, bracket increment, and delta % per bracket. Changes here update the flow table and chart.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={() => {
                const rate = parseFloat(getCalculatorValue('67RR', 'exchange_rate') as string) || 1.15832;
                const defaults = JSON.parse(JSON.stringify(defaultConfig));
                defaults.threshold_config!['67RR']!.target_total_incl_vat = Math.round(180000 * rate);
                defaults.threshold_config!['67RR']!.target_currency = 'GBP';
                defaults.threshold_config!['67RR']!.target_total_incl_vat_orig = 180000;
                defaults.monthly_host_payment = 9000;
                defaults.host_payment_vat_rate_ireland = 0.135;
                defaults.host_payment_vat_rate_uk = 0.20;
                setConfig(defaults);
              }}
            >
              Reset to defaults
            </Button>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(Number(e.target.value))}>
                {[2024, 2025, 2026, 2027].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {saveStatus === 'saving' && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloudQueueIcon fontSize="small" /> Saving variables…
              </Typography>
            )}
            {saveStatus === 'saved' && (
              <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon fontSize="small" /> Saved
              </Typography>
            )}
            {saveStatus === 'pending' && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloudQueueIcon fontSize="small" /> Unsaved changes
              </Typography>
            )}
          </Box>
        </Box>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Monthly host payment</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Monthly payment (gross, incl VAT)"
              value={(config.monthly_host_payment ?? 9000).toLocaleString()}
              onChange={(e) => setConfig(prev => ({ ...prev, monthly_host_payment: parseCurrency(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
              inputProps={{ style: { textAlign: 'right' } }}
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              label="VAT rate (Ireland)"
              value={((config.host_payment_vat_rate_ireland ?? 0.135) * 100).toFixed(1)}
              onChange={(e) => setConfig(prev => ({ ...prev, host_payment_vat_rate_ireland: parseFloat(e.target.value) / 100 || 0 }))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              inputProps={{ style: { textAlign: 'right' } }}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="VAT rate (UK)"
              value={((config.host_payment_vat_rate_uk ?? 0.20) * 100).toFixed(1)}
              onChange={(e) => setConfig(prev => ({ ...prev, host_payment_vat_rate_uk: parseFloat(e.target.value) / 100 || 0 }))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              inputProps={{ style: { textAlign: 'right' } }}
              sx={{ width: 140 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Used for the Annual Payment calculation: bonus = Host fee − amount already paid during the year.
          </Typography>
        </Paper>
        <Grid container spacing={1} sx={{ mb: 2, alignItems: 'stretch' }}>
          {PROPERTY_CODES.map((code) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={code} sx={{ display: 'flex' }}>
              <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'action.hover', '& .MuiCardContent-root': { py: 1, px: 1.5, '&:last-child': { pb: 1 }, flex: 1 } }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>{code}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Agreed hosting rate %"
                      value={getThresholdConfigValue(code, 'agreed_hosting_rate_pct')}
                      onChange={(e) => updateThresholdConfig(code, 'agreed_hosting_rate_pct', parseFloat(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                    <TextField
                      size="small"
                      label="Agreed hosting rate % discount"
                      value={getThresholdConfigValue(code, 'agreed_hosting_rate_discount_pct')}
                      onChange={(e) => updateThresholdConfig(code, 'agreed_hosting_rate_discount_pct', parseFloat(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="Target Total to Owners (incl VAT)"
                        value={(() => {
                          const curr = (getThresholdConfigValue(code, 'target_currency') as string) || GROSS_CURRENCY[code];
                          const orig = parseFloat(getThresholdConfigValue(code, 'target_total_incl_vat_orig') as string);
                          const eur = parseFloat(getThresholdConfigValue(code, 'target_total_incl_vat') as string) || 0;
                          if (curr === 'GBP' && orig > 0) return orig ? fmtCurrency(orig, 'GBP') : '';
                          return eur ? fmtCurrency(eur, 'EUR') : '';
                        })()}
                        onChange={(e) => {
                          const curr = (getThresholdConfigValue(code, 'target_currency') as string) || GROSS_CURRENCY[code];
                          const val = parseCurrency(e.target.value);
                          const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || parseFloat(getCalculatorValue('67RR', 'exchange_rate') as string) || 1.15832;
                          setConfig(prev => {
                            const tc = prev.threshold_config?.[code];
                            if (!tc) return prev;
                            const next = { ...prev, threshold_config: { ...prev.threshold_config!, [code]: { ...tc } } };
                            if (curr === 'GBP') {
                              next.threshold_config![code]!.target_total_incl_vat_orig = val;
                              next.threshold_config![code]!.target_total_incl_vat = Math.round(val * rate);
                            } else {
                              next.threshold_config![code]!.target_total_incl_vat = val;
                              next.threshold_config![code]!.target_total_incl_vat_orig = 0;
                            }
                            return next;
                          });
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{((getThresholdConfigValue(code, 'target_currency') as string) || GROSS_CURRENCY[code]) === 'GBP' ? '£' : '€'}</InputAdornment>
                        }}
                        inputProps={{ style: { textAlign: 'right' } }}
                        sx={{ flex: 1 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 70 }}>
                        <Select
                          value={((getThresholdConfigValue(code, 'target_currency') as string) || GROSS_CURRENCY[code]) as string}
                          onChange={(e) => {
                            const curr = e.target.value as 'EUR' | 'GBP';
                            const rate = parseFloat(getCalculatorValue(code, 'exchange_rate') as string) || parseFloat(getCalculatorValue('67RR', 'exchange_rate') as string) || 1.15832;
                            setConfig(prev => {
                              const tc = prev.threshold_config?.[code];
                              if (!tc) return prev;
                              const next = { ...prev, threshold_config: { ...prev.threshold_config!, [code]: { ...tc, target_currency: curr } } };
                              if (curr === 'GBP') {
                                const orig = tc.target_total_incl_vat_orig ?? (tc.target_total_incl_vat ? Math.round(tc.target_total_incl_vat / rate) : 180000);
                                next.threshold_config![code]!.target_total_incl_vat_orig = orig;
                                next.threshold_config![code]!.target_total_incl_vat = Math.round(orig * rate);
                              } else {
                                const eurVal = tc.target_total_incl_vat ?? Math.round(180000 * rate);
                                next.threshold_config![code]!.target_total_incl_vat_orig = 0;
                                next.threshold_config![code]!.target_total_incl_vat = eurVal;
                              }
                              return next;
                            });
                          }}
                          sx={{ height: 40 }}
                        >
                          <MenuItem value="EUR">EUR</MenuItem>
                          <MenuItem value="GBP">GBP</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <TextField
                      size="small"
                      label="Bracket increment (€)"
                      value={(() => { const v = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 0; return v ? fmtCurrency(v, 'EUR') : ''; })()}
                      onChange={(e) => updateThresholdConfig(code, 'bracket_increment', parseCurrency(e.target.value))}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                    <TextField
                      size="small"
                      label="Delta % per bracket"
                      value={getThresholdConfigValue(code, 'delta_per_10k')}
                      onChange={(e) => updateThresholdConfig(code, 'delta_per_10k', parseFloat(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </Box>
                  {(() => {
                    const ratePct = parseFloat(getThresholdConfigValue(code, 'agreed_hosting_rate_pct') as string) || 20;
                    const discountPct = parseFloat(getThresholdConfigValue(code, 'agreed_hosting_rate_discount_pct') as string) || 0;
                    const targetInclVat = parseFloat(getThresholdConfigValue(code, 'target_total_incl_vat') as string) || 0;
                    const vatRate = parseFloat(getCalculatorValue(code, 'vat_rate') as string) || 0.20;
                    const bracketIncr = parseFloat(getThresholdConfigValue(code, 'bracket_increment') as string) || 10000;
                    const delta = parseFloat(getThresholdConfigValue(code, 'delta_per_10k') as string) || 1;
                    const discountedRate = Math.round((ratePct - discountPct) * 10) / 10;
                    const targetExVat = targetInclVat > 0 ? Math.round(targetInclVat * (1 - vatRate)) : 0;
                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }} component="span">
                        The agreed (discounted) hosting rate of <strong>{discountedRate}%</strong> will apply to the Gross Target Amount of <strong>{fmtCurrency(targetInclVat, 'EUR')}</strong> [<strong>{fmtCurrency(targetExVat, 'EUR')}</strong> Net of VAT] for this property. For every <strong>{fmtCurrency(bracketIncr, 'EUR')}</strong> above this target the fee will increase by <strong>{delta}%</strong>. Similarly, for every <strong>{fmtCurrency(bracketIncr, 'EUR')}</strong> below this target, the fee will decrease by <strong>{delta}%</strong>.
                      </Typography>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
          Performance is calculated in €10,000 brackets (e.g. €94,000 is treated as the €90,000 bracket). The fee increases by 1% when the next €10,000 bracket is reached.
        </Typography>
      </TabPanel>
    </Box>
  );
};
